import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { Calendar, MapPin, ArrowRight, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUserStore } from '../stores/StoreProvider';

const OnboardingBirthdayZipcode = observer(() => {
  const navigate = useNavigate();
  const userStore = useUserStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Store birthday and zipcode in onboarding data
      userStore.onboardingData = {
        ...userStore.onboardingData,
        birthdate: data.birthday,
        zipcode: data.zipcode,
      };
      
      console.log('Birthday and zipcode saved to onboarding data:', data);
      
      toast.success('Information saved!');
      navigate('/onboarding/team-organization');
    } catch (error) {
      console.error('Error saving information:', error);
      toast.error('Failed to save information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-2xl">B</span>
              </div>
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-white mb-2">
            Tell us about yourself
          </h2>
          <p className="text-blue-100">
            Help us find local teams and events
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Birthday */}
            <div>
              <label htmlFor="birthday" className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline mr-2" size={16} />
                Birthday
              </label>
              <input
                {...register('birthday', { 
                  required: 'Birthday is required',
                  validate: (value) => {
                    const today = new Date();
                    const birthDate = new Date(value);
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    
                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                      age--;
                    }
                    
                    if (age < 5 || age > 100) {
                      return 'Please enter a valid birthday';
                    }
                    return true;
                  }
                })}
                type="date"
                id="birthday"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.birthday && (
                <p className="mt-1 text-sm text-red-600">{errors.birthday.message}</p>
              )}
            </div>

            {/* Zipcode */}
            <div>
              <label htmlFor="zipcode" className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="inline mr-2" size={16} />
                Zip Code
              </label>
              <input
                {...register('zipcode', { 
                  required: 'Zip code is required',
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: 'Please enter a valid zip code'
                  }
                })}
                type="text"
                id="zipcode"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="12345"
                maxLength={10}
              />
              {errors.zipcode && (
                <p className="mt-1 text-sm text-red-600">{errors.zipcode.message}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => navigate('/onboarding/role')}
                className="flex-1 flex justify-center items-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="mr-2" size={20} />
                Back
              </button>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 flex justify-center items-center px-4 py-3 border border-transparent text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="ml-2" size={20} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Progress */}
        <div className="text-center">
          <div className="flex justify-center space-x-2">
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
          </div>
          <p className="text-white/80 text-sm mt-2">Step 4 of 5</p>
        </div>
      </div>
    </div>
  );
});

export default OnboardingBirthdayZipcode; 